// EmailCampaign.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { apiService } from '../services/apiService';
import {
  Mail, Send, AlertCircle, TrendingUp,
  Users, CheckCircle, XCircle, Clock,
  Eye, Settings, RefreshCw, Zap
} from 'lucide-react';

const EmailCampaign = () => {
  // Core State
  const [stats, setStats] = useState({
    total_profiles: 0,
    sent: 0,
    pending: 0,
    failed: 0,
    campaigns: []
  });

  // Campaign State
  const [campaignName, setCampaignName] = useState('');
  const [campaignLimit, setCampaignLimit] = useState('');
  const [testMode, setTestMode] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [campaignPreviews, setCampaignPreviews] = useState([]);
  const [showPreviewsModal, setShowPreviewsModal] = useState(false);

  // Unsent Profiles State
  const [unsentProfiles, setUnsentProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Test Email State
  const [testEmail, setTestEmail] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);

  // UI State
  const [activeTab, setActiveTab] = useState('overview');
  const [toasts, setToasts] = useState([]);
  const [showSettings, setShowSettings] = useState(false);

  // Toast Management
  const showToast = useCallback((type, message, duration = 5000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, duration);
  }, []);

  // Fetch Email Stats
  const fetchEmailStats = useCallback(async () => {
    try {
      const data = await apiService.getEmailStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching email stats:', error);
      showToast('error', 'Failed to fetch email statistics');
    }
  }, [showToast]);

  // Fetch Unsent Profiles
  const fetchUnsentProfiles = useCallback(async () => {
    try {
      const data = await apiService.getUnsentProfiles(50);
      setUnsentProfiles(data.profiles || []);
      showToast('success', `Loaded ${data.profiles?.length || 0} unsent profiles`);
    } catch (error) {
      console.error('Error fetching unsent profiles:', error);
      showToast('error', 'Failed to fetch unsent profiles');
    }
  }, [showToast]);

  // Preview Email for Profile
  const previewEmailForProfile = useCallback(async (profile) => {
    setIsLoadingPreview(true);
    setSelectedProfile(profile);
    try {
      const data = await apiService.previewEmail(profile.id);
      setEmailPreview(data.email);
      showToast('success', 'Email preview generated');
    } catch (error) {
      console.error('Error previewing email:', error);
      showToast('error', 'Failed to generate email preview');
    } finally {
      setIsLoadingPreview(false);
    }
  }, [showToast]);

  // Send Email Campaign
  const handleSendCampaign = useCallback(async () => {
    if (!campaignName.trim()) {
      showToast('error', 'Please enter a campaign name');
      return;
    }

    const limit = campaignLimit ? parseInt(campaignLimit) : null;

    if (campaignLimit && (isNaN(limit) || limit < 1)) {
      showToast('error', 'Please enter a valid limit (positive number)');
      return;
    }

    const confirmMessage = testMode
      ? `Start TEST campaign "${campaignName}"? (No emails will be sent)`
      : `Start LIVE campaign "${campaignName}"? This will send ${limit || 'all unsent'} emails!`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsSending(true);
    try {
      const response = await apiService.sendEmailCampaign(campaignName, limit, testMode);

      // In test mode, show previews
      if (testMode && response.previews && response.previews.length > 0) {
        setCampaignPreviews(response.previews);
        setShowPreviewsModal(true);
        showToast('success', `Test complete! ${response.previews.length} email previews generated`);
      } else {
        showToast('success', response.message || 'Campaign started successfully');
      }

      // Refresh stats after campaign
      setTimeout(() => {
        fetchEmailStats();
        fetchUnsentProfiles();
      }, 2000);
    } catch (error) {
      console.error('Error sending campaign:', error);
      showToast('error', `Failed to start campaign: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  }, [campaignName, campaignLimit, testMode, showToast, fetchEmailStats, fetchUnsentProfiles]);

  // Send Test Email
  const handleSendTest = useCallback(async () => {
    if (!testEmail.trim() || !testEmail.includes('@')) {
      showToast('error', 'Please enter a valid email address');
      return;
    }

    setIsSendingTest(true);
    try {
      const response = await apiService.sendTestEmail(testEmail);
      showToast('success', response.message || 'Test email sent successfully');
      setTestEmail('');
    } catch (error) {
      console.error('Error sending test email:', error);
      showToast('error', `Failed to send test email: ${error.message}`);
    } finally {
      setIsSendingTest(false);
    }
  }, [testEmail, showToast]);

  // Auto-fetch stats on mount
  useEffect(() => {
    fetchEmailStats();
  }, [fetchEmailStats]);

  // Auto-load unsent profiles when Profiles tab is opened
  useEffect(() => {
    if (activeTab === 'profiles' && unsentProfiles.length === 0) {
      fetchUnsentProfiles();
    }
  }, [activeTab, unsentProfiles.length, fetchUnsentProfiles]);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-blue-600">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Mail className="h-8 w-8 text-white" />
                <div>
                  <h1 className="text-2xl font-bold text-white">Email Campaign Manager</h1>
                  <p className="text-purple-100 text-sm">AI-Powered Professional Outreach</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowSettings(prev => !prev)}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  title="Settings"
                >
                  <Settings className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-6">
            {/* Tabs */}
            <div className="flex space-x-4 mb-6">
              <TabButton
                active={activeTab === 'overview'}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </TabButton>
              <TabButton
                active={activeTab === 'campaign'}
                onClick={() => setActiveTab('campaign')}
              >
                Send Campaign
              </TabButton>
              <TabButton
                active={activeTab === 'profiles'}
                onClick={() => setActiveTab('profiles')}
              >
                Unsent Profiles ({stats.pending})
              </TabButton>
              <TabButton
                active={activeTab === 'test'}
                onClick={() => setActiveTab('test')}
              >
                Test Email
              </TabButton>
            </div>

            {/* Tab Content */}
            <div className="mt-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard
                      title="Total Profiles"
                      value={stats.total_profiles}
                      icon={<Users className="h-6 w-6" />}
                      color="blue"
                      bgColor="bg-blue-50"
                      textColor="text-blue-600"
                      borderColor="border-blue-200"
                    />
                    <StatCard
                      title="Emails Sent"
                      value={stats.sent}
                      icon={<CheckCircle className="h-6 w-6" />}
                      color="green"
                      bgColor="bg-green-50"
                      textColor="text-green-600"
                      borderColor="border-green-200"
                    />
                    <StatCard
                      title="Pending"
                      value={stats.pending}
                      icon={<Clock className="h-6 w-6" />}
                      color="yellow"
                      bgColor="bg-yellow-50"
                      textColor="text-yellow-600"
                      borderColor="border-yellow-200"
                    />
                    <StatCard
                      title="Failed"
                      value={stats.failed}
                      icon={<XCircle className="h-6 w-6" />}
                      color="red"
                      bgColor="bg-red-50"
                      textColor="text-red-600"
                      borderColor="border-red-200"
                    />
                  </div>

                  {/* Success Rate */}
                  <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                    <div className="flex items-center gap-3 mb-4">
                      <TrendingUp className="h-6 w-6 text-purple-600" />
                      <h3 className="text-lg font-semibold">Campaign Performance</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-600">Success Rate</span>
                          <span className="font-medium">
                            {stats.sent > 0
                              ? Math.round((stats.sent / (stats.sent + stats.failed)) * 100)
                              : 0}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-green-500 h-3 rounded-full transition-all duration-500"
                            style={{
                              width: `${stats.sent > 0
                                ? Math.round((stats.sent / (stats.sent + stats.failed)) * 100)
                                : 0}%`
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-600">Completion Rate</span>
                          <span className="font-medium">
                            {stats.total_profiles > 0
                              ? Math.round((stats.sent / stats.total_profiles) * 100)
                              : 0}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-blue-500 h-3 rounded-full transition-all duration-500"
                            style={{
                              width: `${stats.total_profiles > 0
                                ? Math.round((stats.sent / stats.total_profiles) * 100)
                                : 0}%`
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Campaigns List */}
                  {stats.campaigns && stats.campaigns.length > 0 && (
                    <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                      <h3 className="text-lg font-semibold mb-4">Recent Campaigns</h3>
                      <div className="space-y-2">
                        {stats.campaigns.map((campaign, index) => (
                          <div key={index} className="p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium text-gray-900">{campaign}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Refresh Button */}
                  <div className="flex justify-center">
                    <button
                      onClick={fetchEmailStats}
                      className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600
                               transition-colors flex items-center gap-2"
                    >
                      <RefreshCw className="h-5 w-5" />
                      Refresh Statistics
                    </button>
                  </div>
                </div>
              )}

              {/* Send Campaign Tab */}
              {activeTab === 'campaign' && (
                <div className="space-y-6">
                  {/* Campaign Configuration */}
                  <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Zap className="h-5 w-5 text-yellow-500" />
                      Campaign Configuration
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Campaign Name
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., Q1 Outreach, VP Operations Campaign"
                          value={campaignName}
                          onChange={(e) => setCampaignName(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Limit (Optional)
                        </label>
                        <input
                          type="number"
                          placeholder="Leave empty to send to all unsent profiles"
                          value={campaignLimit}
                          onChange={(e) => setCampaignLimit(e.target.value)}
                          min="1"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Number of emails to send (default: all unsent)
                        </p>
                      </div>

                      <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <input
                          type="checkbox"
                          id="testMode"
                          checked={testMode}
                          onChange={(e) => setTestMode(e.target.checked)}
                          className="w-5 h-5 rounded border-gray-300"
                        />
                        <label htmlFor="testMode" className="flex-1 cursor-pointer">
                          <span className="font-medium text-gray-900">Test Mode</span>
                          <p className="text-sm text-gray-600">
                            Preview what would be sent without actually sending emails
                          </p>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Warning */}
                  {!testMode && (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-3">
                      <AlertCircle className="h-5 w-5" />
                      <div>
                        <p className="font-medium">Live Campaign Mode</p>
                        <p className="text-sm">Emails will be sent to real recipients. Make sure your SMTP credentials are configured.</p>
                      </div>
                    </div>
                  )}

                  {/* Send Button */}
                  <div className="flex gap-4">
                    <button
                      onClick={handleSendCampaign}
                      disabled={isSending || !campaignName.trim()}
                      className="flex-1 px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg
                               hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50
                               disabled:cursor-not-allowed font-medium text-lg flex items-center justify-center gap-3"
                    >
                      {isSending ? (
                        <>
                          <RefreshCw className="h-6 w-6 animate-spin" />
                          Sending Campaign...
                        </>
                      ) : (
                        <>
                          <Send className="h-6 w-6" />
                          {testMode ? 'Run Test Campaign' : 'Send Live Campaign'}
                        </>
                      )}
                    </button>
                  </div>

                  {/* Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">How it works:</h4>
                    <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                      <li>AI generates personalized emails for each profile using Gemini 2.5</li>
                      <li>Emails are sent with 30-second delays to avoid spam filters</li>
                      <li>Database automatically marks sent profiles to prevent duplicates</li>
                      <li>Professional signature and formatting applied to all emails</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Unsent Profiles Tab */}
              {activeTab === 'profiles' && (
                <div className="space-y-6">
                  {/* Controls */}
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">
                      Profiles Ready for Outreach ({unsentProfiles.length})
                    </h3>
                    <button
                      onClick={fetchUnsentProfiles}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600
                               transition-colors flex items-center gap-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Reload
                    </button>
                  </div>

                  {/* Profiles List */}
                  {unsentProfiles.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No unsent profiles found</p>
                      <p className="text-sm mt-2">All profiles have been contacted or no profiles available</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {unsentProfiles.map(profile => (
                        <div key={profile.id} className="bg-white rounded-lg shadow p-4 border border-gray-200 hover:border-blue-300 transition-colors">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="font-medium text-gray-900">{profile.name}</h4>
                              <p className="text-sm text-gray-500">{profile.email}</p>
                            </div>
                            <button
                              onClick={() => previewEmailForProfile(profile)}
                              disabled={isLoadingPreview}
                              className="p-2 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                              title="Preview Email"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="space-y-1 text-sm text-gray-600">
                            {profile.title && (
                              <p><span className="font-medium">Title:</span> {profile.title}</p>
                            )}
                            {profile.company && (
                              <p><span className="font-medium">Company:</span> {profile.company}</p>
                            )}
                            {profile.location && (
                              <p><span className="font-medium">Location:</span> {profile.location}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Email Preview Modal */}
                  {emailPreview && selectedProfile && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                      <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
                          <div className="flex justify-between items-center">
                            <h2 className="text-xl font-bold">Email Preview</h2>
                            <button
                              onClick={() => {
                                setEmailPreview(null);
                                setSelectedProfile(null);
                              }}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                        <div className="p-6 space-y-4">
                          <div>
                            <p className="text-sm font-medium text-gray-600">To:</p>
                            <p className="text-gray-900">{selectedProfile.email}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Subject:</p>
                            <p className="text-gray-900 font-medium">{emailPreview.subject}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600 mb-2">Body:</p>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 whitespace-pre-wrap font-mono text-sm">
                              {emailPreview.body}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Test Email Tab */}
              {activeTab === 'test' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold mb-4">Send Test Email</h3>
                    <p className="text-gray-600 mb-4">
                      Verify your SMTP configuration by sending a test email
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Test Email Address
                        </label>
                        <input
                          type="email"
                          placeholder="your.email@example.com"
                          value={testEmail}
                          onChange={(e) => setTestEmail(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <button
                        onClick={handleSendTest}
                        disabled={isSendingTest || !testEmail.trim()}
                        className="w-full px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600
                                 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium
                                 flex items-center justify-center gap-2"
                      >
                        {isSendingTest ? (
                          <>
                            <RefreshCw className="h-5 w-5 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="h-5 w-5" />
                            Send Test Email
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <SettingsModal 
          onClose={() => setShowSettings(false)} 
          showToast={showToast}
        />
      )}
      {/* Campaign Previews Modal */}
      {showPreviewsModal && campaignPreviews.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-y-auto my-4">
            <div className="sticky top-0 bg-white p-6 border-b border-gray-200 z-10">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Test Campaign Preview</h2>
                  <p className="text-gray-600 mt-1">{campaignPreviews.length} emails would be sent</p>
                </div>
                <button
                  onClick={() => setShowPreviewsModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold px-3 py-1 rounded hover:bg-gray-100"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {campaignPreviews.map((preview, index) => (
                <div key={index} className="border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm">
                  {/* Preview Header */}
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 border-b border-gray-200">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                            #{index + 1}
                          </span>
                          <h3 className="font-bold text-lg text-gray-900">{preview.name || 'No Name'}</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="font-medium text-gray-600">To:</span>
                            <span className="ml-2 text-blue-600">{preview.to}</span>
                          </div>
                          {preview.title && (
                            <div>
                              <span className="font-medium text-gray-600">Title:</span>
                              <span className="ml-2">{preview.title}</span>
                            </div>
                          )}
                          {preview.company && (
                            <div>
                              <span className="font-medium text-gray-600">Company:</span>
                              <span className="ml-2">{preview.company}</span>
                            </div>
                          )}
                          {preview.industry && (
                            <div>
                              <span className="font-medium text-gray-600">Industry:</span>
                              <span className="ml-2 text-purple-600">{preview.industry}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* AI Score */}
                      {preview.personalization_score && preview.personalization_score !== 'N/A' && (
                        <div className="flex-shrink-0 text-center">
                          <div className="bg-green-500 text-white rounded-lg px-4 py-2">
                            <div className="text-2xl font-bold">{preview.personalization_score}/10</div>
                            <div className="text-xs">AI Score</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Email Content */}
                  <div className="p-6 space-y-4">
                    {/* Subject Line */}
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-2">Subject Line (Selected):</p>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="font-bold text-gray-900">{preview.subject}</p>
                      </div>
                    </div>

                    {/* A/B Variants */}
                    {preview.subject_variants && preview.subject_variants.length > 1 && (
                      <div>
                        <p className="text-sm font-semibold text-gray-600 mb-2">
                          All A/B Test Variants ({preview.subject_variants.length}):
                        </p>
                        <div className="space-y-2">
                          {preview.subject_variants.map((variant, vIdx) => (
                            <div
                              key={vIdx}
                              className={`p-2 rounded border text-sm ${
                                variant === preview.subject
                                  ? 'bg-green-50 border-green-300 font-medium'
                                  : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              {vIdx + 1}. {variant}
                              {variant === preview.subject && <span className="ml-2 text-green-600">(✓ Selected)</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Email Body */}
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-2">Email Body:</p>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <pre className="whitespace-pre-wrap font-sans text-sm text-gray-900 leading-relaxed">
                          {preview.body}
                        </pre>
                      </div>
                    </div>

                    {/* AI Insights */}
                    {preview.insights && Object.keys(preview.insights).length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="font-semibold text-blue-900 mb-3">AI Insights:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          {preview.insights.detected_pain_points && (
                            <div>
                              <span className="font-medium text-blue-700">Pain Points:</span>
                              <p className="text-blue-900 mt-1">
                                {Array.isArray(preview.insights.detected_pain_points)
                                  ? preview.insights.detected_pain_points.join(', ')
                                  : preview.insights.detected_pain_points}
                              </p>
                            </div>
                          )}
                          {preview.insights.value_proposition && (
                            <div>
                              <span className="font-medium text-blue-700">Value Prop:</span>
                              <p className="text-blue-900 mt-1">{preview.insights.value_proposition}</p>
                            </div>
                          )}
                          {preview.insights.psychological_trigger && (
                            <div>
                              <span className="font-medium text-blue-700">Trigger:</span>
                              <p className="text-blue-900 mt-1">{preview.insights.psychological_trigger}</p>
                            </div>
                          )}
                          {preview.insights.social_proof_used && (
                            <div>
                              <span className="font-medium text-blue-700">Social Proof:</span>
                              <p className="text-blue-900 mt-1">{preview.insights.social_proof_used}</p>
                            </div>
                          )}
                          {preview.insights.cta_type && (
                            <div>
                              <span className="font-medium text-blue-700">CTA Type:</span>
                              <p className="text-blue-900 mt-1">{preview.insights.cta_type}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 p-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <p className="text-gray-600">
                  These are AI-generated previews. No emails were sent.
                </p>
                <button
                  onClick={() => setShowPreviewsModal(false)}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Messages */}
      <div className="fixed bottom-4 right-4 space-y-2 z-50">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`p-4 rounded-lg shadow-lg text-white ${
              toast.type === 'error' ? 'bg-red-500' :
              toast.type === 'success' ? 'bg-green-500' :
              'bg-blue-500'
            }`}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper Components
const TabButton = ({ children, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
      active
        ? 'bg-blue-500 text-white shadow-md'
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`}
  >
    {children}
  </button>
);

const StatCard = ({ title, value, icon, bgColor, textColor, borderColor }) => (
  <div className={`${bgColor} rounded-lg p-6 border ${borderColor}`}>
    <div className="flex items-center gap-4">
      <div className={textColor}>{icon}</div>
      <div>
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <p className={`text-3xl font-bold ${textColor}`}>{value}</p>
      </div>
    </div>
  </div>
);


const SettingsModal = ({ onClose, showToast }) => {
  const [config, setConfig] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: '587',
    smtp_user: '',
    smtp_password: '', // Won't receive actual password back, just has_password flag
    from_name: '',
    from_email: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await apiService.getEmailConfig();
        if (response.config) {
          setConfig(prev => ({
            ...prev,
            ...response.config,
            smtp_password: '' // Clear password field
          }));
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        showToast('error', 'Failed to load email settings');
      } finally {
        setIsLoading(false);
      }
    };
    loadSettings();
  }, [showToast]);

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await apiService.saveEmailConfig(config);
      showToast('success', 'Email configuration saved successfully');
      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('error', 'Failed to save settings: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!config.smtp_user) {
      showToast('error', 'Please enter an SMTP user (email)');
      return;
    }

    setIsTesting(true);
    try {
      // Create a temporary test email request
      await apiService.sendTestEmail(config.smtp_user);
      showToast('success', 'Connection successful! Test email sent to ' + config.smtp_user);
    } catch (error) {
      console.error('Test connection failed:', error);
      showToast('error', 'Connection failed: ' + error.message);
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Settings className="h-5 w-5 text-gray-500" />
            Email Configuration
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
             <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Host</label>
              <input
                type="text"
                value={config.smtp_host}
                onChange={e => setConfig({...config, smtp_host: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="smtp.gmail.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
              <input
                type="text"
                value={config.smtp_port}
                onChange={e => setConfig({...config, smtp_port: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="587"
              />
            </div>
          </div>

          <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">SMTP User (Email)</label>
             <input
               type="email"
               value={config.smtp_user}
               onChange={e => setConfig({...config, smtp_user: e.target.value})}
               className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
               placeholder="you@company.com"
               required
             />
          </div>

          <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">
               SMTP Password (App Password)
             </label>
             <input
               type="password"
               value={config.smtp_password}
               onChange={e => setConfig({...config, smtp_password: e.target.value})}
               className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
               placeholder={config.has_password ? "•••••••• (Stored)" : "Enter App Password"}
             />
             <p className="text-xs text-gray-500 mt-1">
               For Gmail, use an <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">App Password</a>.
             </p>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="font-medium text-gray-900 mb-3">Sender Identity</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Name</label>
                <input
                  type="text"
                  value={config.from_name}
                  onChange={e => setConfig({...config, from_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Email</label>
                <input
                  type="email"
                  value={config.from_email}
                  onChange={e => setConfig({...config, from_email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="john@company.com"
                />
              </div>
             </div>
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={isTesting || !config.smtp_user}
              className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
            >
              {isTesting ? 'Testing...' : 'Test Connection'}
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailCampaign;
